import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";
import { useSearchParams } from "react-router";
import Select from "react-select";
import { toast } from "react-toastify";
import { FaChevronLeft, FaChevronRight, FaDownload } from "react-icons/fa";
import { createPlan, deactivatePlan, deletePlan, listAllClients, listAllPlans, listFields, listPlansPage, updatePlan } from "../../../utils/cdesApi";
import { customStyles } from "../../../utils/styleUtils";
import ConfirmationDialog from "../../shared/ConfirmationDialog";
import FormCheckbox from "../../shared/FormCheckbox";
import { columnCellProps } from "./catalogColumns";
import { planGridLayout } from "./planViewColumns";
import PlanFormDialog from "./PlanFormDialog";
import { downloadCsv } from "./cdesCsv";
import "./CdesGrid.scss";

const PAGE_SIZE = 50;

const isBlank = (value) => value == null || value === "";

const displayValue = (value) => (isBlank(value) ? "—" : String(value));

const STATUS_TONES = { ACTIVE: "ok", INACTIVE: "muted" };

const ACTIONS_COLUMN = { key: "actions", label: "Actions", frozen: true, width: 280 };

const planKey = (plan) => `${plan.client?.clientId ?? ""}:${plan.planId ?? ""}`;
const planLabel = (plan) => plan?.displayName || plan?.planId || "This plan";

const PlanView = () => {
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const clientId = searchParams.get("clientId") || "";
    const [cursorStack, setCursorStack] = useState([undefined]);
    const cursor = cursorStack[cursorStack.length - 1];
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState("");
    const [formMode, setFormMode] = useState(null);
    const [editingPlan, setEditingPlan] = useState(null);
    const [formError, setFormError] = useState("");
    const [saving, setSaving] = useState(false);
    const [deactivateTarget, setDeactivateTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [reactivatingId, setReactivatingId] = useState("");
    const visibilityForm = useForm({
        defaultValues: { showInactiveClients: false, showInactivePlans: false },
    });
    const showInactiveClients = useWatch({ control: visibilityForm.control, name: "showInactiveClients" });
    const showInactivePlans = useWatch({ control: visibilityForm.control, name: "showInactivePlans" });

    const clientsQuery = useQuery({
        queryKey: ["cdes-clients", showInactiveClients],
        queryFn: () => listAllClients({ includeInactive: showInactiveClients }),
    });
    const fieldsQuery = useQuery({
        queryKey: ["cdes-field-columns", "plan"],
        queryFn: () => listFields({ scope: "plan" }),
    });
    const layout = useMemo(
        () => planGridLayout(fieldsQuery.data?.fields, [ACTIONS_COLUMN]),
        [fieldsQuery.data?.fields],
    );
    const cellProps = (column, extraClassName) => columnCellProps(column, layout, extraClassName);

    const plansQuery = useQuery({
        queryKey: ["cdes-plans", clientId, cursor, showInactiveClients, showInactivePlans],
        queryFn: () =>
            listPlansPage({
                clientId,
                cursor,
                limit: PAGE_SIZE,
                includeInactiveClients: Boolean(showInactiveClients),
                includeInactivePlans: Boolean(showInactivePlans),
            }),
    });

    const plans = plansQuery.data?.plans ?? [];
    const nextCursor = plansQuery.data?.nextCursor;

    const selectedClientName = useMemo(() => {
        const match = (clientsQuery.data ?? []).find((client) => client.clientId === clientId);
        return match?.displayName || clientId;
    }, [clientsQuery.data, clientId]);

    const clientOptions = useMemo(
        () =>
            (clientsQuery.data ?? []).map((client) => ({
                value: client.clientId,
                label: client.displayName || client.clientId,
            })),
        [clientsQuery.data],
    );

    const matchedClientOption = clientOptions.find((option) => option.value === clientId);
    const clientNameResolved = Boolean(matchedClientOption) || !clientsQuery.isLoading;
    const selectedClientOption =
        clientId && clientNameResolved
            ? matchedClientOption ?? { value: clientId, label: selectedClientName }
            : null;

    const resetVisibilityPage = () => {
        setCursorStack([undefined]);
        setExportError("");
    };

    const setClient = (nextClientId) => {
        setCursorStack([undefined]);
        setExportError("");
        const next = new URLSearchParams(searchParams);
        if (nextClientId) next.set("clientId", nextClientId);
        else next.delete("clientId");
        setSearchParams(next);
    };

    const exportCsv = async () => {
        setExporting(true);
        setExportError("");
        try {
            const rows = await listAllPlans({
                clientId,
                includeInactiveClients: Boolean(showInactiveClients),
                includeInactivePlans: Boolean(showInactivePlans),
            });
            downloadCsv({
                columns: layout.columns,
                rows,
                filename: clientId ? `cdes-plans-${clientId}.csv` : "cdes-plans.csv",
            });
        } catch (error) {
            setExportError(error.message || "Export failed");
        } finally {
            setExporting(false);
        }
    };

    const page = cursorStack.length;
    const hasPages = page > 1 || Boolean(nextCursor);
    const planCountLabel = `${plans.length} ${plans.length === 1 ? "plan" : "plans"}`;

    const refreshPlans = () =>
        Promise.all([
            queryClient.invalidateQueries({ queryKey: ["cdes-plans"] }),
            queryClient.invalidateQueries({ queryKey: ["cdes-clients"] }),
            queryClient.invalidateQueries({ queryKey: ["cdes-client-rows"] }),
        ]);

    const openCreate = () => {
        setFormError("");
        setEditingPlan(null);
        setFormMode("create");
    };

    const openEdit = (plan) => {
        setFormError("");
        setEditingPlan(plan);
        setFormMode("edit");
    };

    const closeForm = () => {
        if (saving) return;
        setFormMode(null);
        setEditingPlan(null);
        setFormError("");
    };

    const submitForm = async (body) => {
        setSaving(true);
        setFormError("");
        try {
            if (formMode === "create") await createPlan(body);
            else await updatePlan(editingPlan.client.clientId, editingPlan.planId, body);
            await refreshPlans();
            toast.success(formMode === "create" ? "Plan created." : "Plan updated.");
            setFormMode(null);
            setEditingPlan(null);
        } catch (error) {
            setFormError(error.message || "Unable to save the plan.");
        } finally {
            setSaving(false);
        }
    };

    const confirmDeactivate = async () => {
        try {
            await deactivatePlan(deactivateTarget.client.clientId, deactivateTarget.planId);
            await refreshPlans();
            toast.success("Plan deactivated.");
        } catch (error) {
            toast.error(error.message || "Unable to deactivate the plan.");
            throw error;
        }
    };

    const confirmDelete = async () => {
        try {
            await deletePlan(deleteTarget.client.clientId, deleteTarget.planId);
            await refreshPlans();
            toast.success("Plan deleted.");
        } catch (error) {
            toast.error(error.message || "Unable to delete the plan.");
            throw error;
        }
    };

    const reactivatePlan = async (plan) => {
        const key = planKey(plan);
        setReactivatingId(key);
        try {
            await updatePlan(plan.client.clientId, plan.planId, { status: "ACTIVE" });
            await refreshPlans();
            toast.success("Plan reactivated.");
        } catch (error) {
            toast.error(error.message || "Unable to reactivate the plan.");
        } finally {
            setReactivatingId("");
        }
    };

    const renderActions = (plan) => {
        const inactive = plan.status === "INACTIVE";
        const name = planLabel(plan);
        const busy = reactivatingId === planKey(plan) || deleteTarget?.planId === plan.planId;
        return (
            <td key="actions" {...cellProps(ACTIONS_COLUMN)}>
                <div className="cdes-client-actions">
                    <button
                        type="button"
                        className="outline-btn"
                        onClick={() => openEdit(plan)}
                        disabled={busy}
                        aria-label={`Edit ${name}`}
                    >
                        Edit
                    </button>
                    {inactive ? (
                        <button
                            type="button"
                            className="outline-btn"
                            onClick={() => reactivatePlan(plan)}
                            disabled={busy}
                            aria-label={`Reactivate ${name}`}
                        >
                            {reactivatingId === planKey(plan) ? "Reactivating…" : "Reactivate"}
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="outline-btn"
                            onClick={() => setDeactivateTarget(plan)}
                            aria-label={`Deactivate ${name}`}
                        >
                            Deactivate
                        </button>
                    )}
                    <button
                        type="button"
                        className="outline-btn"
                        onClick={() => setDeleteTarget(plan)}
                        disabled={busy}
                        aria-label={`Delete ${name}`}
                    >
                        Delete
                    </button>
                </div>
            </td>
        );
    };

    const renderCell = (plan, column) => {
        if (column.key === "actions") return renderActions(plan);
        const value = column.value(plan);
        if (isBlank(value)) {
            return (
                <td key={column.key} {...cellProps(column, "is-empty")}>
                    —
                </td>
            );
        }
        const text = displayValue(value);
        const tone = column.key === "status" ? STATUS_TONES[text] : undefined;
        return (
            <td key={column.key} {...cellProps(column)} title={text}>
                {column.key === "status" ? (
                    <span className={`cdes-grid__status cdes-grid__status--${tone || "muted"}`}>
                        {text}
                    </span>
                ) : (
                    text
                )}
            </td>
        );
    };

    let body;
    if (plansQuery.isLoading) {
        body = (
            <div className="loader-wrapper">
                <div className="loader" />
            </div>
        );
    } else if (plansQuery.isError) {
        body = (
            <div className="cdes-grid__state">
                <p className="danger">{plansQuery.error.message || "Unable to load plans."}</p>
            </div>
        );
    } else if (plans.length === 0) {
        body = (
            <div className="cdes-grid__state">
                <p className="cdes-grid__state-title">No plans found</p>
                <p>
                    {clientId
                        ? `${selectedClientName} has no ${showInactivePlans ? "" : "active "}plans.`
                        : showInactiveClients || showInactivePlans
                          ? "No plans match this view."
                          : "There are no active plans to display yet."}
                </p>
            </div>
        );
    } else {
        body = (
            <div className="table-wrapper cdes-grid__scroll">
                <table className="cdes-grid__table" style={{ width: layout.tableWidth, "--cdes-frozen-width": `${layout.frozenWidth}px` }}>
                    <colgroup>
                        {layout.tableColumns.map((column) => (
                            <col key={column.key} style={{ width: column.width }} />
                        ))}
                    </colgroup>
                    <thead>
                        <tr>
                            {layout.displayFrozen.map((column) => (
                                <th key={column.key} rowSpan={2} {...cellProps(column)} title={column.label}>
                                    {column.label}
                                </th>
                            ))}
                            {layout.categoryGroups.map((group) => (
                                <th
                                    key={group.category}
                                    colSpan={group.columns.length}
                                    className="cdes-grid__group is-group-start"
                                >
                                    <span className="cdes-grid__group-label">{group.category}</span>
                                </th>
                            ))}
                        </tr>
                        <tr>
                            {layout.categoryGroups.flatMap((group) =>
                                group.columns.map((column) => (
                                    <th
                                        key={column.key}
                                        {...cellProps(column, "cdes-grid__column")}
                                        title={column.label}
                                    >
                                        {column.label}
                                    </th>
                                )),
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {plans.map((plan, index) => {
                            const clientKey = plan.client?.clientId ?? "";
                            const previousClientKey = plans[index - 1]?.client?.clientId ?? "";
                            const startsClientGroup = index === 0 || clientKey !== previousClientKey;
                            return (
                                <tr
                                    key={`${clientKey}-${plan.planId}`}
                                    className={startsClientGroup ? "is-client-group-start" : undefined}
                                >
                                    {layout.tableColumns.map((column) => renderCell(plan, column))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    return (
        <div className="cdes-grid">
            <div className="cdes-grid__header">
                <div>
                    <p className="title">Plans</p>
                    <p className="sub-title">
                        CDES plans with their configured fields, one row per plan.
                    </p>
                </div>
                <div className="cdes-grid__header-actions">
                    <button type="button" className="edit-btn cdes-grid__btn" onClick={openCreate}>
                        + Add Plan
                    </button>
                    <button
                        type="button"
                        className="outline-btn cdes-grid__btn"
                        onClick={exportCsv}
                        disabled={exporting || plansQuery.isLoading}
                    >
                        {exporting ? <span className="loader loader--inline" /> : <FaDownload />}
                        {exporting ? "Exporting…" : "Export CSV"}
                    </button>
                </div>
            </div>

            <div className="cdes-grid__filter-stack">
                <div className="cdes-grid__filters">
                    <div className="cdes-grid__field">
                        <label htmlFor="cdes-grid-client">Client</label>
                        <Select
                            inputId="cdes-grid-client"
                            className="react-select-container"
                            classNamePrefix="react-select"
                            placeholder={clientsQuery.isLoading ? "Loading clients…" : "All clients"}
                            options={clientOptions}
                            value={selectedClientOption}
                            onChange={(option) => setClient(option?.value || "")}
                            isLoading={clientsQuery.isLoading}
                            isDisabled={clientsQuery.isLoading}
                            isClearable
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            styles={{
                                ...customStyles,
                                menuPortal: (base) => ({ ...base, zIndex: 200 }),
                            }}
                        />
                    </div>
                </div>
                    <div className="cdes-grid__checks">
                        <FormCheckbox
                            name="showInactiveClients"
                            label="Show inactive clients"
                            control={visibilityForm.control}
                            onSelectedHandler={resetVisibilityPage}
                        />
                        <FormCheckbox
                            name="showInactivePlans"
                            label="Show inactive plans"
                            control={visibilityForm.control}
                            onSelectedHandler={resetVisibilityPage}
                        />
                    </div>
            </div>

            {clientsQuery.isError && (
                <p className="danger">{clientsQuery.error.message || "Unable to load clients."}</p>
            )}
            {exportError && <p className="danger">{exportError}</p>}

            {(page > 1 || (plansQuery.isSuccess && plans.length > 0)) && (
                <div className="cdes-grid__summary">
                    <span className="loading-with-label">
                        {plansQuery.isSuccess &&
                            (!clientId
                                ? `${planCountLabel} on this page`
                                : clientNameResolved
                                  ? `${planCountLabel} for ${selectedClientName}`
                                  : planCountLabel)}
                        {plansQuery.isFetching && <span className="loader loader--inline" />}
                    </span>
                    {hasPages && (
                        <div className="cdes-grid__pager" aria-label="Plan pages">
                            <button
                                type="button"
                                className="outline-btn cdes-grid__btn"
                                onClick={() => setCursorStack((stack) => stack.slice(0, -1))}
                                disabled={page === 1 || plansQuery.isFetching}
                                aria-label="Previous page"
                            >
                                <FaChevronLeft />
                            </button>
                            <span>Page {page}</span>
                            <button
                                type="button"
                                className="outline-btn cdes-grid__btn"
                                onClick={() => setCursorStack((stack) => [...stack, nextCursor])}
                                disabled={!nextCursor || plansQuery.isFetching}
                                aria-label="Next page"
                            >
                                <FaChevronRight />
                            </button>
                        </div>
                    )}
                </div>
            )}

            {fieldsQuery.isError ? (
                <p className="danger" role="alert">
                    {fieldsQuery.error.message || "Unable to load field columns."}
                </p>
            ) : null}
            {body}
            {formMode ? (
                <PlanFormDialog
                    key={formMode === "edit" ? planKey(editingPlan) : "create"}
                    mode={formMode}
                    plan={editingPlan}
                    clientOptions={clientOptions}
                    defaultClientId={clientId}
                    saving={saving}
                    serverError={formError}
                    onClose={closeForm}
                    onSubmit={submitForm}
                />
            ) : null}
            <ConfirmationDialog
                open={Boolean(deactivateTarget)}
                onOpenChange={(open) => {
                    if (!open) setDeactivateTarget(null);
                }}
                title="Deactivate plan"
                body={`${planLabel(deactivateTarget)} (${deactivateTarget?.planId || "this plan"}) will become inactive. It will be hidden from the active list, and its data will not be deleted.`}
                confirmLabel="Deactivate"
                confirmPendingLabel="Deactivating..."
                onConfirm={confirmDeactivate}
            />
            <ConfirmationDialog
                open={Boolean(deleteTarget)}
                onOpenChange={(open) => {
                    if (!open) setDeleteTarget(null);
                }}
                title="Delete plan"
                body={`${planLabel(deleteTarget)} (${deleteTarget?.planId || "this plan"}) will be permanently deleted. This cannot be undone.`}
                confirmLabel="Delete"
                confirmPendingLabel="Deleting..."
                onConfirm={confirmDelete}
            />
        </div>
    );
};

export default PlanView;
